export interface FlagReview {
  value: number;
  rules?: string[];
  note?: string;
  by?: string;
  at: string;
}

export interface OverrideInfo {
  at: string;
  by?: string;
  note?: string;
}

export type FlagReviewMap = Record<string, FlagReview>;
export type OverrideInfoMap = Record<string, OverrideInfo>;

export const FIXED_CONVERTED_NOTE = 'Converted from Fixed percentage';
export const SYSTEM_ACTOR = 'System';
export const REVIEW_NOTE_MAX = 500;
export const ACTOR_MAX = 200;
const RULE_PATTERN = /^[A-Za-z0-9]{1,20}$/;
const RULES_MAX = 10;

const isNum = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);
const text = (v: unknown, max: number) => (typeof v === 'string' ? v.trim().slice(0, max) : '');
const validAt = (v: unknown) => typeof v === 'string' && !isNaN(Date.parse(v));

export function cellReviewKey(ch: string, blk: string, j: number, t: number): string {
  return ch + '|' + blk + '|' + j + '|' + t;
}

function cellKeyOk(key: string): boolean {
  const parts = key.split('|');
  return parts.length === 4 && !!parts[0] && !!parts[1] && /^\d+$/.test(parts[2]) && /^\d+$/.test(parts[3]);
}

function overrideKeyOk(key: string): boolean {
  if (cellKeyOk(key)) return true;
  const parts = key.split('|');
  return parts.length === 3 && parts.every(p => !!p);
}

export function sanitizeFlagReviews(raw: unknown): FlagReviewMap {
  const out: FlagReviewMap = {};
  if (!raw || typeof raw !== 'object') return out;
  Object.keys(raw as Record<string, unknown>).forEach(key => {
    if (!cellKeyOk(key)) return;
    const v = (raw as Record<string, unknown>)[key] as Record<string, unknown> | null;
    if (!v || typeof v !== 'object') return;
    const note = text(v.note, REVIEW_NOTE_MAX);
    if (!isNum(v.value) || v.value < 0 || !validAt(v.at)) return;
    const by = text(v.by, ACTOR_MAX);
    const rules = Array.isArray(v.rules) ? (v.rules as unknown[]).filter((r): r is string => typeof r === 'string' && RULE_PATTERN.test(r)).slice(0, RULES_MAX) : null;
    out[key] = Object.assign({ value: v.value, at: v.at as string }, rules ? { rules } : null, note ? { note } : null, by ? { by } : null);
  });
  return out;
}

export function sanitizeOverrideInfo(raw: unknown): OverrideInfoMap {
  const out: OverrideInfoMap = {};
  if (!raw || typeof raw !== 'object') return out;
  Object.keys(raw as Record<string, unknown>).forEach(key => {
    if (!overrideKeyOk(key)) return;
    const v = (raw as Record<string, unknown>)[key] as Record<string, unknown> | null;
    if (!v || typeof v !== 'object' || !validAt(v.at)) return;
    const by = text(v.by, ACTOR_MAX);
    const note = text(v.note, REVIEW_NOTE_MAX);
    out[key] = Object.assign({ at: v.at as string }, by ? { by } : null, note ? { note } : null);
  });
  return out;
}

export const REVIEW_PRECISION = 1e4;

const gridValue = (v: number) => Math.round(v * REVIEW_PRECISION) / REVIEW_PRECISION;
const sameRules = (a: string[], b: string[]) => a.length === b.length && a.slice().sort().every((x, i) => x === b.slice().sort()[i]);

export function reviewHolds(review: FlagReview | null | undefined, value: number | null | undefined, rules?: string[] | null): boolean {
  return !!review && isNum(value) && reviewReopened(review, value, rules) === null;
}

export function reviewReopened(review: FlagReview | null | undefined, value: number | null | undefined, rules?: string[] | null): { why: 'value' | 'rules'; before: number | string[]; after: number | string[] | null } | null {
  if (!review) return null;
  if (!isNum(value)) return { why: 'value', before: review.value, after: null };
  if (review.rules) {
    if (gridValue(review.value) !== gridValue(value)) return { why: 'value', before: review.value, after: value };
    if (rules && !sameRules(review.rules, rules)) return { why: 'rules', before: review.rules, after: rules };
    return null;
  }
  return Math.abs(review.value - value) <= 1e-9 * Math.max(1, Math.abs(value)) ? null : { why: 'value', before: review.value, after: value };
}
